// Shared by the backend (address stats) and the Create MoneyFlow form, so keep
// it free of node-only imports.
//
// Address stats list an address's currencies by transfer count over its whole
// history. That alone makes a poor default: spam tokens airdropped to busy
// addresses in bulk (e.g. "nengliang-org" or "Authorize at Synthetix.to" sent
// to the USDT contract on Tron) out-count the real currencies and may not have
// moved for years, so picking one with a recent date range finds nothing. Each
// currency therefore also carries when it moved and whether Bitquery prices
// it, and the form ranks, picks and dates currencies from that.

// v2 address-stats fields of one currency row
export interface CurrencyActivityRow {
    usd?: string | number | null
    Block?: { first_date: string | null, last_date: string | null }
}

export interface CurrencyActivity {
    first_date?: string | null
    last_date?: string | null
    // Bitquery has a USD price for some of its transfers (spam tokens have none)
    priced?: boolean
}

export const toCurrencyActivity = (row: CurrencyActivityRow): CurrencyActivity => ({
    first_date: row.Block?.first_date ?? null,
    last_date: row.Block?.last_date ?? null,
    priced: Number(row.usd) > 0,
})

export interface RankedCurrency extends CurrencyActivity {
    symbol: string
    address: string
    tokenId: string
    count: number
}

// Phishing / spam token names advertise a site ("Authorize at Synthetix.to",
// "claim rewards on t.ly/…", "567u.com") or hide invisible characters to pass
// for a real token ("U\u206FS\u206FDG"). A one-letter suffix is a real
// token's bridge mark ("USDC.e") and is not matched.
const ADVERTISES_SITE = /https?:|www\.|[a-z0-9-]\.[a-z]{2,}\b/i
const INVISIBLE = /[\u00AD\u200B-\u200F\u202A-\u202E\u2060-\u206F\uFEFF]/

// 0: priced, 1: named but unpriced (e.g. a new stablecoin), 2: spam-like (no
// name, a site or hidden characters in the name). Chains without the activity
// fields (v1) rank everything 0 and keep the count order.
export const currencyTier = (currency: RankedCurrency) => {
    const symbol = currency.symbol?.trim() ?? ""
    if (!symbol || ADVERTISES_SITE.test(symbol) || INVISIBLE.test(symbol)) return 2
    return currency.priced === false ? 1 : 0
}

// Address stats list a currency once per direction: one entry each, with the
// counts summed and the dates spanning both
export function mergeCurrencyLists<T extends RankedCurrency>(lists: T[][]): T[] {
    const merged = new Map<string, T>()
    for (const currency of lists.flat()) {
        const key = currency.address + currency.tokenId + currency.symbol
        const seen = merged.get(key)
        if (!seen) {
            merged.set(key, { ...currency })
            continue
        }
        seen.count += currency.count
        seen.priced = seen.priced || currency.priced
        if (currency.first_date && (!seen.first_date || currency.first_date < seen.first_date)) seen.first_date = currency.first_date
        if (currency.last_date && (!seen.last_date || currency.last_date > seen.last_date)) seen.last_date = currency.last_date
    }
    return [...merged.values()]
}

// Real currencies first, spam last; by transfer count within a tier
export const rankCurrencies = <T extends RankedCurrency>(currencies: T[]): T[] =>
    currencies.toSorted((a, b) => currencyTier(a) - currencyTier(b) || b.count - a.count)

const RECENT_MS = 7 * 24 * 60 * 60 * 1000

// Default for the form: the most used real currency among those that moved in
// the last week of the address's activity (that week is what the form then
// traces), else the real currency that moved last. Spam only if nothing else.
export function pickDefaultCurrency<T extends RankedCurrency>(currencies: T[]): T | null {
    if (currencies.length === 0) return null
    const lastMs = (c: T) => (c.last_date ? new Date(c.last_date).getTime() : NaN)
    const real = currencies.filter((c) => currencyTier(c) < 2)
    const pool = real.length > 0 ? real : currencies
    const dated = pool.filter((c) => Number.isFinite(lastMs(c)))
    if (dated.length === 0) return rankCurrencies(pool)[0]

    const latest = Math.max(...dated.map(lastMs))
    const recent = dated.filter((c) => lastMs(c) >= latest - RECENT_MS)
    return rankCurrencies(recent)[0]
}

// Busy currencies get only their last active day (as the address-wide default
// does past this many transfers), so the first trace stays small
const FULL_RANGE_MAX_TRANSFERS = 10000

// Date range in which the currency is known to have moved, or null when the
// chain doesn't report per-currency dates
export function currencyDateRange(currency: RankedCurrency | null | undefined): [string, string] | null {
    if (!currency?.first_date || !currency.last_date) return null
    return currency.count > FULL_RANGE_MAX_TRANSFERS ?
        [currency.last_date, currency.last_date] :
        [currency.first_date, currency.last_date]
}

// The value a flow's `currency` filter takes for this currency: the token
// contract, or the token id / symbol when there is none ("-": native coin)
export const currencyFilterValue = (currency: Pick<RankedCurrency, "symbol" | "address" | "tokenId">) =>
    currency.address && currency.address !== "-" ? currency.address : currency.tokenId || currency.symbol
